import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { getChannel } from '@/lib/channel';
import { resolveFeature, type FeatureKey } from './channelFlags';
import type { OpeningHour } from '@sizilianer/core/features/restaurant/settings';
import { toSupabaseError } from '@/utils/supabaseError';

// 0.11: OpeningHour lebt jetzt in core (tables/types.ts braucht ihn dort);
// Re-Export hält alle bestehenden Importer dieses Moduls gültig.
export type { OpeningHour } from '@sizilianer/core/features/restaurant/settings';

/**
 * Reservierungs-Kapazität pro Wochentag, gespeichert in
 * `reservation_settings_jsonb.capacity_by_day`.
 *
 * Öffnungszeiten und Ruhetage kommen aus `opening_hours_jsonb` — die Kapazität
 * kennt es nicht, deshalb liegt sie hier. Beides zusammen erzeugt Migration
 * 0039 per Trigger zu `capacity_rules`; von Hand wird dort nichts mehr gepflegt.
 */
export type DayCapacity = {
  day: OpeningHour['day'];
  max_guests: number;
  max_reservations: number;
};

export const DEFAULT_DAY_CAPACITY: Omit<DayCapacity, 'day'> = {
  max_guests: 40,
  max_reservations: 12,
};

/** Liest capacity_by_day robust aus dem Settings-JSONB (Reihenfolge egal). */
export function readCapacityByDay(reservationSettings: unknown): DayCapacity[] {
  const raw =
    reservationSettings && typeof reservationSettings === 'object'
      ? (reservationSettings as { capacity_by_day?: unknown }).capacity_by_day
      : undefined;
  const list = Array.isArray(raw) ? (raw as Partial<DayCapacity>[]) : [];
  return WEEKDAYS.map((day) => {
    const hit = list.find((c) => c?.day === day);
    return {
      day,
      max_guests: Number(hit?.max_guests ?? DEFAULT_DAY_CAPACITY.max_guests),
      max_reservations: Number(
        hit?.max_reservations ?? DEFAULT_DAY_CAPACITY.max_reservations,
      ),
    };
  });
}

export type RestaurantSettings = {
  id: number;
  /** D3 (0059): 'slots' = feste capacity_by_day-Werte · 'tables' = aus Tischen. */
  capacity_source?: 'slots' | 'tables';
  online_booking_percent?: number;
  online_booking_buffer_percent?: number;
  name: string;
  email: string;
  email_sender_address: string;
  phone: string;
  address_street: string;
  address_zip: string;
  address_city: string;
  address_country: string;
  legal_name: string | null;
  legal_representative: string | null;
  tax_id: string | null;
  newsletter_lead_magnet: string | null;
  opening_hours_jsonb: OpeningHour[] | unknown;
  socials_jsonb: { facebook?: string; instagram?: string; google?: string } | unknown;
  reservation_settings_jsonb: unknown;
  /** 0066: Kill-Switch fürs Baroon-Chat-Widget (Launcher + Proxy-Config). */
  chatbot_enabled?: boolean;
  /**
   * 0076: Letzter Tag der Gesprächs-Testphase (Berlin, `date`).
   * NULL = aus, es wird kein Chat-Freitext gespeichert. Fail-closed:
   * ein Datum in der Vergangenheit stoppt die Speicherung von selbst.
   */
  chat_logging_until?: string | null;
  /** 0071: Feature-Toggles pro Kanal {key: {web, app}}; fehlender Key ⇒ an. */
  channel_flags?: unknown;
  /**
   * 0114: Server-Not-Aus des Bestellsystems {key: true}, ein Schlüssel je
   * Etappe. 🚨 Die GENAUE UMKEHRUNG von `channel_flags` darüber: fehlender
   * Schlüssel ⇒ AUS, nur exakt `true` ⇒ erteilt. Ausgewertet wird
   * ausschließlich im Server (api/_lib/bestellsystem.ts); dieses Feld dient
   * hier nur der Schalterstellung im Backoffice.
   * Geschrieben NICHT über diesen Hook, sondern über
   * POST /api/admin-bestellsystem-schalter (Audit + Merge).
   */
  bestellsystem_freigaben?: unknown;
  /** 0070: Kill-Switch für den Newsletter auf der Public-Site
   *  (Footer-Strip, /newsletter, Anmelde-API). Bestätigen/Abmelden
   *  bleibt immer erreichbar. */
  newsletter_enabled?: boolean;
  /** 0089: Globaler Reservierungsstopp — seit 0090 NUR noch SQL-Backstop
   *  ohne Admin-UI (die Karte wurde durch die Regel-Art „Geöffnet, keine
   *  Online-Reservierungen" in den Sonderregeln ersetzt). false stoppt
   *  serverseitig weiterhin ALLES sofort; die Public-Site rendert dann
   *  den Vollbild-Hinweis. Schaltbar nur via SQL/Studio. */
  online_reservations_enabled?: boolean;
  /** 0089: Optionaler Gast-Hinweis des globalen Backstops (NULL = i18n-Standard). */
  reservations_stop_message?: string | null;
  /**
   * 2.5 (Migration 0086): darf ein iPad das Recht speichern, den Tag zu
   * öffnen? DEFAULT false — ein Sicherheitsschalter startet aus, und der
   * Server prüft ihn bei jedem Aufruf fail-closed mit `=== true`.
   */
  pass_stored_unlock_enabled?: boolean;
  /** Lebensdauer einer gespeicherten Freischaltung in Tagen (1–365). */
  pass_stored_unlock_ttl_days?: number;
  /**
   * 2.6a (Migration 0087): darf DER PASS das Backoffice in der App
   * öffnen? DEFAULT false — dieser Schalter gibt eine ZWEITE
   * Supabase-Sitzung frei, deshalb startet er aus. Der Server liest ihn
   * bei jedem Aufruf frisch und fail-closed (`api/_lib/passSettings.ts`),
   * Ausschalten wirkt also sofort. Schreiben darf ihn nur ein Inhaber
   * (Policy `restaurant_settings_admin_update`) — ein Manager darf den
   * Kontor benutzen, aber nicht aufsperren.
   */
  pass_kontor_enabled?: boolean;
  /**
   * 0096: darf sich die Leitung ein persönliches Gerät merken? DEFAULT
   * false — dieser Schalter gibt eine echte Leitungs-Sitzung ohne
   * Passwort frei, deshalb startet er aus. Der Server liest ihn bei jedem
   * Aufruf frisch und fail-closed (`api/_lib/passSettings.ts`);
   * Ausschalten wirkt sofort und stoppt Einrichten UND Entsperren — das
   * schnellste Mittel bei einem verlorenen Gerät.
   */
  device_login_enabled?: boolean;
  /** Gleitende Lebensdauer eines gemerkten Geräts in Tagen (1–90). */
  device_login_ttl_days?: number;
  /**
   * 0097: darf Face ID im Browser (Passkey) zum Anmelden benutzt werden?
   * Eigener Schalter neben `device_login_enabled`, damit sich der jüngere
   * Teil abschalten lässt, ohne den PIN-Weg mitzunehmen.
   */
  webauthn_login_enabled?: boolean;
  /**
   * 0098: Ape-Rechner-Regelwerk (Staffeln, Anfahrt, Origin). Leer oder
   * ungültig ⇒ Rechner AUS — die eine Wahrheit ist der fail-closed-Parser
   * in `@sizilianer/core` (features/ape/apePricingConfig).
   */
  ape_pricing_jsonb?: unknown;
  active: boolean;
  updated_at: string;
};

export function useRestaurantSettings() {
  return useQuery({
    queryKey: ['restaurant-settings'],
    queryFn: async (): Promise<RestaurantSettings | null> => {
      const { data, error } = await supabase
        .from('restaurant_settings')
        .select('*')
        .eq('active', true)
        .limit(1)
        .maybeSingle();
      if (error) throw toSupabaseError(error);
      return (data ?? null) as unknown as RestaurantSettings | null;
    },
    staleTime: 5 * 60_000,
  });
}

export function useUpdateRestaurantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      patch: Partial<Omit<RestaurantSettings, 'id' | 'updated_at'>>;
    }) => {
      const { error } = await supabase
        .from('restaurant_settings')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update(args.patch as any)
        .eq('id', args.id);
      if (error) throw toSupabaseError(error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['restaurant-settings'] });
    },
  });
}

/**
 * Kanal-Feature-Abfrage (0071). Default-on: solange Settings laden oder der
 * Key fehlt, gilt das Feature als AKTIV — Toggles schalten ab, nie frei.
 * Neue Flags: Registry in channelFlags.ts, Anleitung in CLAUDE.md
 * „Kanal-Architektur". Wirkt auf anderen Geräten erst nach Refetch
 * (staleTime 5 min) — Minuten, nicht Sekunden.
 */
export function useFeature(key: FeatureKey): boolean {
  const { data } = useRestaurantSettings();
  return resolveFeature(data?.channel_flags, getChannel(), key);
}

export const WEEKDAY_LABELS: Record<OpeningHour['day'], string> = {
  mon: 'Montag',
  tue: 'Dienstag',
  wed: 'Mittwoch',
  thu: 'Donnerstag',
  fri: 'Freitag',
  sat: 'Samstag',
  sun: 'Sonntag',
};

export const WEEKDAYS: Array<OpeningHour['day']> = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
];
